import React, { useCallback, useEffect, useState } from 'react';
import { setSettingsDirty } from "../../../../../util/settingsDirty";
import { useTranslation } from 'react-i18next';
import './BusinessCalendarComponent.css';
import { useAuth0 } from '../../../../../auth/useAuth0';
import { useBusinessContext } from '../../../../../context/BusinessContext';
import { useAlert } from '../../../../../context/alert/AlertContext';
import {
    StripeAccountStatus,
    createStripeDashboardLink,
    createStripeOnboardingLink,
    getStripeAccountStatus,
    refreshStripeAccountStatus,
} from '../../../../../service/payment/payment-service';
import {
    PaymentMethod,
    methodsForCountry,
} from '../../../../../model/payment/Transaction';
import {
    findBusinessDetailsByBusinessId,
    saveAcceptedPaymentMethods,
    saveDiscountRates,
} from '../../../../../service/business/business-service';
import {
    getBusinessHealthPlans,
    saveHealthPlan,
    deleteHealthPlan,
} from '../../../../../service/business/health-plan-service';
import { HealthPlan } from '../../../../../model/business/HealthPlan';
import { useConfirm } from '../../../../../context/confirm/ConfirmContext';
import { SettingsField, NumberWithSuffix } from '../../../../commons/settings-row/SettingsRow';
import ActionsButtonsComponent from '../../../../commons/action-buttons/ActionsButtonsComponent';
import { ONLINE_PAYMENTS_ENABLED } from '../../../../../util/onlinePayments';

/**
 * Major Brazilian health plans surfaced as quick-add suggestions. The catalog
 * itself is per-business (stored in business-service); these are just one-click
 * shortcuts — the business can still type any custom plan name.
 */
const HEALTH_PLAN_SUGGESTIONS = [
    'Unimed', 'Amil', 'Bradesco Saúde', 'SulAmérica', 'Hapvida',
    'NotreDame Intermédica', 'Porto Seguro', 'Golden Cross', 'Prevent Senior',
];

/**
 * Default ISO-3166 alpha-2 country code for Stripe Connect. Must match the
 * country of the platform's Stripe account (cross-country isn't allowed
 * by Stripe). Future: read from the Business country setting + a platform-
 * country whitelist.
 */
const DEFAULT_COUNTRY = 'BR';

/** Bank-transfer details are short (bank/branch/account/holder) — cap them so the
 *  field can't take a wall of text (#4). Mirrored by the backend guard. */
const BANK_INFO_MAX = 500;

const BusinessPaymentComponent: React.FC = () => {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness, setSelectedBusiness, allBusiness, setAllBusiness } =
        useBusinessContext();
    const { setAlert } = useAlert();
    const confirm = useConfirm();

    const businessId = selectedBusiness?.id ? String(selectedBusiness.id) : '';
    const businessIdNum = selectedBusiness?.id ? Number(selectedBusiness.id) : 0;

    /** Health plans (convênios) the business accepts. Persisted immediately on
     *  add/remove (no draft/save banner — it's a curated list, not a form). */
    const [healthPlans, setHealthPlans] = useState<HealthPlan[]>([]);
    const [healthPlansLoading, setHealthPlansLoading] = useState(false);
    const [newPlanName, setNewPlanName] = useState('');
    const [planBusy, setPlanBusy] = useState(false);

    const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | null>(null);
    const [stripeLoading, setStripeLoading] = useState(false);
    const [stripeConnecting, setStripeConnecting] = useState(false);

    /** Methods this business has chosen to accept. Empty set means "accept all". */
    const [acceptedMethods, setAcceptedMethods] = useState<Set<PaymentMethod>>(new Set());
    /** Snapshot used to detect "is modified" for the unsaved-changes banner. */
    const [originalAcceptedMethods, setOriginalAcceptedMethods] =
        useState<Set<PaymentMethod>>(new Set());
    const [methodsLoading, setMethodsLoading] = useState(false);
    const [methodsSaving, setMethodsSaving] = useState(false);
    /** Per-method payout details (PIX key / bank info) — saved with the toggles. */
    const [pixKey, setPixKey] = useState('');
    const [bankTransferInfo, setBankTransferInfo] = useState('');
    const [originalPixKey, setOriginalPixKey] = useState('');
    const [originalBankTransferInfo, setOriginalBankTransferInfo] = useState('');
    /** Cached business details — used to pass through unchanged fields on save. */
    const [businessDetailsCache, setBusinessDetailsCache] = useState<any>(null);

    /** Discount-allowed rates (moved here from Business Details — they belong with
     *  payments). Strings so the inputs can be cleared; saved as numbers. */
    type DiscountRates = { def: string; r1: string; r2: string; r3: string; r4: string };
    const emptyRates: DiscountRates = { def: '', r1: '', r2: '', r3: '', r4: '' };
    const [discountRates, setDiscountRates] = useState<DiscountRates>(emptyRates);
    const [originalDiscountRates, setOriginalDiscountRates] = useState<DiscountRates>(emptyRates);
    const [discountSaving, setDiscountSaving] = useState(false);
    const isDiscountModified = JSON.stringify(discountRates) !== JSON.stringify(originalDiscountRates);

    const saveRates = async () => {
        if (!businessId) return;
        setDiscountSaving(true);
        try {
            const token = await getAccessTokenSilently();
            const num = (s: string) => (s.trim() === '' ? null : Number(s));
            const result = await saveDiscountRates(
                businessId,
                {
                    discountAllowedRateDefault: num(discountRates.def),
                    discountAllowedRate1: num(discountRates.r1),
                    discountAllowedRate2: num(discountRates.r2),
                    discountAllowedRate3: num(discountRates.r3),
                    discountAllowedRate4: num(discountRates.r4),
                },
                token
            );
            if (result.success) {
                setAlert('success', t('settings.business.alerts.discountSaved'));
                setOriginalDiscountRates(discountRates);
            } else {
                setAlert('danger', result.error ?? t('settings.business.alerts.discountSaveError'));
            }
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('settings.business.alerts.discountSaveError'));
        } finally {
            setDiscountSaving(false);
        }
    };

    /** True when the user toggled at least one method since load/last save. */
    const isMethodsModified = (() => {
        if (acceptedMethods.size !== originalAcceptedMethods.size) return true;
        // Array.from avoids requiring ES2015+ Set iteration.
        const a = Array.from(acceptedMethods);
        for (const m of a) {
            if (!originalAcceptedMethods.has(m)) return true;
        }
        if (pixKey !== originalPixKey) return true;
        if (bankTransferInfo !== originalBankTransferInfo) return true;
        return false;
    })();

    /** Methods this business COUNTRY allows (PIX = Brazil, e-Transfer = Canada). */
    const availableMethods = methodsForCountry(
        businessDetailsCache?.country ?? selectedBusiness?.country
    );

    const loadStripeStatus = useCallback(async () => {
        if (!businessId) return;
        try {
            setStripeLoading(true);
            const token = await getAccessTokenSilently();
            const status = await getStripeAccountStatus(businessId, token);
            setStripeStatus(status);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[Stripe] status fetch failed:', e);
        } finally {
            setStripeLoading(false);
        }
    }, [businessId, getAccessTokenSilently]);

    useEffect(() => {
        loadStripeStatus();
    }, [loadStripeStatus]);

    /**
     * Pull the current accepted-methods string from business details and
     * cache the full details for the upcoming save (we have to pass through
     * unchanged fields since the mutation replaces the row).
     */
    const loadAcceptedMethods = useCallback(async () => {
        if (!businessIdNum) return;
        try {
            setMethodsLoading(true);
            const token = await getAccessTokenSilently();
            const details = await findBusinessDetailsByBusinessId(businessIdNum, token);
            setBusinessDetailsCache(details);
            const s = (v: any) => (v === null || v === undefined ? '' : String(v));
            const dr: DiscountRates = {
                def: s(details?.discountAllowedRateDefault),
                r1: s(details?.discountAllowedRate1),
                r2: s(details?.discountAllowedRate2),
                r3: s(details?.discountAllowedRate3),
                r4: s(details?.discountAllowedRate4),
            };
            setDiscountRates(dr);
            setOriginalDiscountRates(dr);
            const raw = details?.acceptedPaymentMethods;
            const allowed = methodsForCountry(details?.country);
            let loaded: Set<PaymentMethod>;
            if (raw) {
                loaded = new Set<PaymentMethod>();
                raw.split(',').map((s: string) => s.trim()).filter(Boolean).forEach((code: string) => {
                    if (allowed.includes(code as PaymentMethod)) {
                        loaded.add(code as PaymentMethod);
                    }
                });
                if (loaded.size === 0) loaded = new Set(allowed);
            } else {
                // Empty in DB → accept all (for this country) by default.
                loaded = new Set(allowed);
            }
            setAcceptedMethods(loaded);
            // Snapshot so isMethodsModified can flip to true on first toggle.
            setOriginalAcceptedMethods(new Set(loaded));
            const pk = details?.pixKey ?? '';
            const bti = details?.bankTransferInfo ?? '';
            setPixKey(pk); setOriginalPixKey(pk);
            setBankTransferInfo(bti); setOriginalBankTransferInfo(bti);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[AcceptedMethods] load failed:', e);
        } finally {
            setMethodsLoading(false);
        }
    }, [businessIdNum, getAccessTokenSilently]);

    useEffect(() => {
        loadAcceptedMethods();
    }, [loadAcceptedMethods]);

    const loadHealthPlans = useCallback(async () => {
        if (!businessIdNum) return;
        try {
            setHealthPlansLoading(true);
            const token = await getAccessTokenSilently();
            const plans = await getBusinessHealthPlans(businessIdNum, token);
            setHealthPlans(plans);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[HealthPlans] load failed:', e);
        } finally {
            setHealthPlansLoading(false);
        }
    }, [businessIdNum, getAccessTokenSilently]);

    useEffect(() => {
        loadHealthPlans();
    }, [loadHealthPlans]);

    /** Add a plan by name (typed input or suggestion chip). Idempotent by name
     *  on the backend, so re-adding an existing plan is a no-op that just merges. */
    const addHealthPlan = async (rawName: string) => {
        const name = rawName.trim();
        if (!name || !businessId || planBusy) return;
        // Skip if already present (case-insensitive) — avoids a needless round-trip.
        if (healthPlans.some((p) => p.name.toLowerCase() === name.toLowerCase())) {
            setNewPlanName('');
            return;
        }
        try {
            setPlanBusy(true);
            const token = await getAccessTokenSilently();
            const saved = await saveHealthPlan({ businessId, name }, token);
            setHealthPlans((prev) =>
                prev.some((p) => p.id === saved.id)
                    ? prev
                    : [...prev, saved].sort((a, b) => a.name.localeCompare(b.name))
            );
            setNewPlanName('');
            setAlert('success', t('settings.payment.healthPlansAdded'));
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('settings.payment.healthPlansError'));
        } finally {
            setPlanBusy(false);
        }
    };

    const removeHealthPlan = async (plan: HealthPlan) => {
        if (!businessId || planBusy) return;
        const ok = await confirm({
            title: t('settings.payment.healthPlansRemove'),
            message: t('settings.payment.healthPlansRemoveConfirm', { name: plan.name }),
            danger: true,
        });
        if (!ok) return;
        try {
            setPlanBusy(true);
            const token = await getAccessTokenSilently();
            await deleteHealthPlan(businessId, plan.id, token);
            setHealthPlans((prev) => prev.filter((p) => p.id !== plan.id));
            setAlert('success', t('settings.payment.healthPlansRemoved'));
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('settings.payment.healthPlansError'));
        } finally {
            setPlanBusy(false);
        }
    };

    const toggleAcceptedMethod = (m: PaymentMethod) => {
        setAcceptedMethods((prev) => {
            const next = new Set(prev);
            if (next.has(m)) next.delete(m);
            else next.add(m);
            return next;
        });
    };

    const saveAcceptedMethods = async () => {
        if (!businessDetailsCache) {
            setAlert('danger', t('settings.business.alerts.detailsNotReady'));
            return;
        }
        if (acceptedMethods.size === 0) {
            setAlert('danger', t('settings.business.alerts.selectAtLeastOneMethod'));
            return;
        }
        try {
            setMethodsSaving(true);
            const token = await getAccessTokenSilently();
            const sorted = availableMethods.filter((m) => acceptedMethods.has(m)).join(',');
            const pk = pixKey.trim();
            const bti = bankTransferInfo.trim();
            const next = { ...businessDetailsCache, acceptedPaymentMethods: sorted, pixKey: pk, bankTransferInfo: bti };
            // Dedicated mutation gated by PAYMENT_SETTINGS (not the broad saveDetails).
            const result = await saveAcceptedPaymentMethods(businessId, sorted, token, pk || null, bti || null);
            if (result.success) {
                setAlert('success', t('settings.business.alerts.methodsSaved'));
                setBusinessDetailsCache(next);
                // New clean baseline so the unsaved-changes banner disappears.
                setOriginalAcceptedMethods(new Set(acceptedMethods));
                setPixKey(pk); setOriginalPixKey(pk);
                setBankTransferInfo(bti); setOriginalBankTransferInfo(bti);
                // Mirror the change into BusinessContext so the Calendar and the
                // Payments screen (both read from selectedBusiness.acceptedPaymentMethods)
                // pick up the new list without a full reload.
                if (selectedBusiness) {
                    const updatedBusiness = {
                        ...selectedBusiness,
                        acceptedPaymentMethods: sorted,
                        pixKey: pk || null,
                        bankTransferInfo: bti || null,
                    };
                    setSelectedBusiness(updatedBusiness);
                    if (allBusiness) {
                        setAllBusiness(
                            allBusiness.map((b) =>
                                b.id === selectedBusiness.id ? updatedBusiness : b
                            )
                        );
                    }
                }
            } else {
                setAlert('danger', `Failed to save: ${result.error}`);
            }
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : 'Failed to save');
        } finally {
            setMethodsSaving(false);
        }
    };

    /** Revert toggles + payout details to the snapshot loaded from the backend. */
    const discardAcceptedMethods = () => {
        setAcceptedMethods(new Set(originalAcceptedMethods));
        setPixKey(originalPixKey);
        setBankTransferInfo(originalBankTransferInfo);
    };

    /** Single Save/Cancel for the whole Payment tab (accepted methods +
     *  discount rates) — uses the shared ActionsButtonsComponent like the other
     *  Settings tabs, instead of a separate per-section button. */
    const handleSavePaymentTab = async () => {
        if (isMethodsModified) await saveAcceptedMethods();
        if (isDiscountModified) await saveRates();
    };
    const handleCancelPaymentTab = () => {
        discardAcceptedMethods();
        setDiscountRates(originalDiscountRates);
    };
    const paymentTabModified = isMethodsModified || isDiscountModified;
    // QA 2026-07-04: mirror the unsaved-changes state so the Settings tab
    // shells can warn before switching away (util/settingsDirty).
    useEffect(() => {
        setSettingsDirty(paymentTabModified);
        return () => setSettingsDirty(false);
    }, [paymentTabModified]);
    const paymentTabSaving = methodsSaving || discountSaving;

    // Re-check status when the tab regains focus. Common flow: user opens
    // Stripe onboarding in a new tab, finishes there, switches back here
    // and expects this view to reflect the latest state.
    useEffect(() => {
        const onFocus = () => {
            if (businessId) refreshFromStripe();
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId]);

    const refreshFromStripe = useCallback(async () => {
        if (!businessId) return;
        try {
            setStripeLoading(true);
            const token = await getAccessTokenSilently();
            const fresh = await refreshStripeAccountStatus(businessId, token);
            setStripeStatus(fresh);
        } catch (e) {
            setAlert(
                'danger',
                e instanceof Error ? e.message : t('settings.business.alerts.stripeRefreshError')
            );
        } finally {
            setStripeLoading(false);
        }
    }, [businessId, getAccessTokenSilently, setAlert]);

    // When the user returns from Stripe (?stripe=return) re-pull live status.
    useEffect(() => {
        if (!businessId) return;
        const url = new URL(window.location.href);
        if (url.searchParams.get('stripe') !== 'return') return;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const fresh = await refreshStripeAccountStatus(businessId, token);
                setStripeStatus(fresh);
                setAlert('success', t('settings.business.alerts.stripeRefreshed'));
            } catch (e) {
                setAlert(
                    'danger',
                    e instanceof Error ? e.message : t('settings.business.alerts.stripeRefreshError')
                );
            } finally {
                url.searchParams.delete('stripe');
                window.history.replaceState({}, '', url.toString());
            }
        })();
    }, [businessId, getAccessTokenSilently, setAlert]);

    const startStripeOnboarding = async () => {
        if (!businessId) {
            setAlert('danger', t('settings.business.alerts.noBusinessSelected'));
            return;
        }
        try {
            setStripeConnecting(true);
            const token = await getAccessTokenSilently();
            const returnUrl = `${window.location.origin}/home?stripe=return`;
            const refreshUrl = `${window.location.origin}/home?stripe=refresh`;
            const url = await createStripeOnboardingLink(
                businessId,
                DEFAULT_COUNTRY,
                returnUrl,
                refreshUrl,
                token
            );
            // Open the Stripe-hosted form in a new tab — keeps the app
            // context on this tab so the user can come back after finishing
            // (Stripe redirects to ?stripe=return which the effect catches).
            window.open(url, '_blank', 'noopener,noreferrer');
            setStripeConnecting(false);
        } catch (e) {
            setAlert(
                'danger',
                e instanceof Error ? e.message : t('settings.business.alerts.stripeOnboardingError')
            );
            setStripeConnecting(false);
        }
    };

    const openStripeDashboard = async () => {
        if (!businessId) return;
        try {
            const token = await getAccessTokenSilently();
            const url = await createStripeDashboardLink(businessId, token);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e) {
            setAlert(
                'danger',
                e instanceof Error ? e.message : t('settings.business.alerts.stripeDashboardError')
            );
        }
    };

    const stripeReady =
        stripeStatus?.connected &&
        stripeStatus?.chargesEnabled &&
        stripeStatus?.payoutsEnabled;
    const stripePartial = stripeStatus?.connected && !stripeReady;

    return (
        <div className="side-content-vertical">
            {/* Same unsaved-changes pattern as the other Settings tabs.
                Shows up only when at least one method toggle differs from
                the loaded snapshot. */}
            <ActionsButtonsComponent
                isModified={paymentTabModified && !paymentTabSaving}
                onSave={handleSavePaymentTab}
                onCancel={handleCancelPaymentTab}
            />
            {/* ---------- Accepted Payment Methods ---------- */}
            <div className="business-section-container separated-container">
                <h3 className="section-header">{t('settings.payment.acceptedMethodsTitle')}</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--gs-text-muted)', marginBottom: '0.75rem' }}>
                    {t('settings.payment.acceptedMethodsHint')}
                </div>
                {methodsLoading ? (
                    <div style={{ color: 'var(--gs-text-soft)' }}>{t('settings.payment.loadingMethods')}</div>
                ) : (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                            gap: '0.5rem 1rem',
                            marginBottom: '0.85rem',
                        }}
                    >
                        {availableMethods.map((m) => (
                            <label
                                key={m}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    color: 'var(--gs-text-body)',
                                    margin: 0,
                                    fontWeight: 500,
                                }}
                            >
                                {/* Styling comes from the global checkbox rule in
                                    BusinessFormsPolish.css so it matches the rest
                                    of Settings. */}
                                <input
                                    type="checkbox"
                                    data-cy={`biz-payment-method-${m.toLowerCase().replace(/_/g, '-')}-toggle`}
                                    checked={acceptedMethods.has(m)}
                                    onChange={() => toggleAcceptedMethod(m)}
                                />
                                {t(`payments.method.${m}`)}
                            </label>
                        ))}
                    </div>
                )}
                {/* Payout details — one aligned sub-card per accepted method that
                    needs data, stacked so heights never fight each other. */}
                {!methodsLoading && (acceptedMethods.has('PIX') || acceptedMethods.has('BANK_TRANSFER')) && (
                    <div className="pay-detail-list">
                        {acceptedMethods.has('PIX') && (
                            <div className="pay-detail" data-cy="biz-payment-pix-detail">
                                <div className="pay-detail__title">{t('payments.method.PIX')}</div>
                                <label className="pay-detail__label" htmlFor="pixKey">
                                    {t('settings.payment.pixKeyLabel')}
                                </label>
                                <input id="pixKey" type="text" className="form-control" maxLength={140}
                                    value={pixKey} onChange={(e) => setPixKey(e.target.value)}
                                    placeholder={t('settings.payment.pixKeyPlaceholder')}
                                    data-cy="biz-payment-pix-key" />
                                <small className="form-text text-muted">{t('settings.payment.pixKeyHint')}</small>
                            </div>
                        )}
                        {acceptedMethods.has('BANK_TRANSFER') && (
                            <div className="pay-detail" data-cy="biz-payment-bank-detail">
                                <div className="pay-detail__title">{t('payments.method.BANK_TRANSFER')}</div>
                                <label className="pay-detail__label" htmlFor="bankInfo">
                                    {t('settings.payment.bankInfoLabel')}
                                </label>
                                <textarea id="bankInfo" className="form-control" rows={3} maxLength={BANK_INFO_MAX}
                                    value={bankTransferInfo}
                                    onChange={(e) => setBankTransferInfo(e.target.value.slice(0, BANK_INFO_MAX))}
                                    placeholder={t('settings.payment.bankInfoPlaceholder')}
                                    data-cy="biz-payment-bank-info" />
                                <div className="gs-char-count" data-cy="biz-payment-bank-info-count"
                                    style={{ textAlign: 'right', fontSize: '0.78rem',
                                        color: bankTransferInfo.length >= BANK_INFO_MAX ? 'var(--gs-danger, #dc3545)' : 'var(--gs-text-muted, #6c757d)' }}>
                                    {bankTransferInfo.length}/{BANK_INFO_MAX}
                                </div>
                                <small className="form-text text-muted">{t('settings.payment.bankInfoHint')}</small>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="business-section-container separated-container">
                <h3 className="section-header">{t('settings.payment.discountTitle')}</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--gs-text-muted)', marginBottom: '0.75rem' }}>
                    {t('settings.payment.discountHint')}
                </div>
                <div className="gs-form-grid">
                    <SettingsField label={t('settings.payment.discountDefault')} htmlFor="discDef">
                        <NumberWithSuffix id="discDef" value={discountRates.def}
                            onChange={(v) => setDiscountRates((p) => ({ ...p, def: v }))} suffix="%" max={100} placeholder="0" />
                    </SettingsField>
                    <SettingsField label={t('settings.payment.discountLevel1')} htmlFor="disc1">
                        <NumberWithSuffix id="disc1" value={discountRates.r1}
                            onChange={(v) => setDiscountRates((p) => ({ ...p, r1: v }))} suffix="%" max={100} placeholder="0" />
                    </SettingsField>
                    <SettingsField label={t('settings.payment.discountLevel2')} htmlFor="disc2">
                        <NumberWithSuffix id="disc2" value={discountRates.r2}
                            onChange={(v) => setDiscountRates((p) => ({ ...p, r2: v }))} suffix="%" max={100} placeholder="0" />
                    </SettingsField>
                    <SettingsField label={t('settings.payment.discountLevel3')} htmlFor="disc3">
                        <NumberWithSuffix id="disc3" value={discountRates.r3}
                            onChange={(v) => setDiscountRates((p) => ({ ...p, r3: v }))} suffix="%" max={100} placeholder="0" />
                    </SettingsField>
                    <SettingsField label={t('settings.payment.discountLevel4')} htmlFor="disc4">
                        <NumberWithSuffix id="disc4" value={discountRates.r4}
                            onChange={(v) => setDiscountRates((p) => ({ ...p, r4: v }))} suffix="%" max={100} placeholder="0" />
                    </SettingsField>
                </div>
            </div>

            {/* ---------- Accepted Health Plans (convênios) ---------- */}
            <div className="business-section-container separated-container">
                <h3 className="section-header">{t('settings.payment.healthPlansTitle')}</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--gs-text-muted)', marginBottom: '0.4rem' }}>
                    {t('settings.payment.healthPlansHint')}
                </div>
                {/* #5: convênios persist immediately (per-plan CRUD), not via the
                   tab's Save/Discard — say so to remove the confusion. */}
                <div className="gs-inline-note" data-cy="biz-health-plans-autosave"
                    style={{ fontSize: '0.78rem', color: 'var(--gs-text-soft)', marginBottom: '0.75rem', fontStyle: 'italic' }}>
                    {t('settings.payment.healthPlansAutoSave')}
                </div>

                {healthPlansLoading ? (
                    <div style={{ color: 'var(--gs-text-soft)' }}>{t('settings.payment.healthPlansLoading')}</div>
                ) : (
                    <>
                        {/* Current plans as removable chips */}
                        {healthPlans.length === 0 ? (
                            <div style={{ fontSize: '0.85rem', color: 'var(--gs-text-soft)', marginBottom: '0.85rem' }}>
                                {t('settings.payment.healthPlansEmpty')}
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.85rem' }}>
                                {healthPlans.map((p) => (
                                    <span
                                        key={p.id}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.4rem',
                                            padding: '0.3rem 0.35rem 0.3rem 0.7rem',
                                            borderRadius: '999px',
                                            background: 'var(--gs-surface-2)',
                                            border: '1px solid var(--gs-border)',
                                            fontSize: '0.85rem',
                                            color: 'var(--gs-text-body)',
                                            fontWeight: 500,
                                        }}
                                    >
                                        {p.name}
                                        <button
                                            type="button"
                                            data-cy={`biz-health-plan-remove-${p.id}`}
                                            onClick={() => removeHealthPlan(p)}
                                            disabled={planBusy}
                                            aria-label={t('settings.payment.healthPlansRemove')}
                                            title={t('settings.payment.healthPlansRemove')}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                border: 'none',
                                                background: 'transparent',
                                                color: 'var(--gs-text-soft)',
                                                cursor: planBusy ? 'default' : 'pointer',
                                                fontSize: '1rem',
                                                lineHeight: 1,
                                                padding: 0,
                                            }}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Add by name */}
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                            <input
                                type="text"
                                className="form-control"
                                data-cy="biz-health-plan-name-input"
                                maxLength={255}
                                value={newPlanName}
                                onChange={(e) => setNewPlanName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addHealthPlan(newPlanName);
                                    }
                                }}
                                placeholder={t('settings.payment.healthPlansAddPlaceholder')}
                                style={{ maxWidth: 280 }}
                            />
                            <button
                                type="button"
                                className="save-button"
                                data-cy="biz-health-plan-add-btn"
                                onClick={() => addHealthPlan(newPlanName)}
                                disabled={planBusy || newPlanName.trim() === ''}
                            >
                                {t('settings.payment.healthPlansAddBtn')}
                            </button>
                        </div>

                        {/* Quick-add suggestions (hide ones already added) */}
                        {(() => {
                            const present = new Set(healthPlans.map((p) => p.name.toLowerCase()));
                            const remaining = HEALTH_PLAN_SUGGESTIONS.filter(
                                (s) => !present.has(s.toLowerCase())
                            );
                            if (remaining.length === 0) return null;
                            return (
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.4rem' }}>
                                    <span style={{ fontSize: '0.8rem', color: 'var(--gs-text-soft)' }}>
                                        {t('settings.payment.healthPlansSuggestions')}
                                    </span>
                                    {remaining.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            data-cy={`biz-health-plan-suggest-${s.toLowerCase().replace(/\s+/g, '-')}`}
                                            onClick={() => addHealthPlan(s)}
                                            disabled={planBusy}
                                            style={{
                                                padding: '0.25rem 0.7rem',
                                                borderRadius: '999px',
                                                border: '1px dashed var(--gs-border)',
                                                background: 'transparent',
                                                color: 'var(--gs-text-body)',
                                                fontSize: '0.8rem',
                                                cursor: planBusy ? 'default' : 'pointer',
                                            }}
                                        >
                                            + {s}
                                        </button>
                                    ))}
                                </div>
                            );
                        })()}
                    </>
                )}
            </div>

            <div className="business-section-container separated-container">
                <h3 className="section-header">{t('settings.payment.stripeTitle')}</h3>
                {/* Platform flag (ONLINE_PAYMENTS_ENABLED): Stripe Connect is hidden until
                    online payments go live. payment-service refuses onboarding too. */}
                {!ONLINE_PAYMENTS_ENABLED && (
                    <div data-cy="biz-payment-stripe-disabled">
                        <span className="gs-stripe-status gs-stripe-status--off">
                            {t('settings.payment.onlineDisabledTitle')}
                        </span>
                        <div
                            style={{
                                marginTop: '0.4rem',
                                fontSize: '0.85rem',
                                color: 'var(--gs-text-muted)',
                            }}
                        >
                            {t('settings.payment.onlineDisabledHint')}
                        </div>
                    </div>
                )}
                {ONLINE_PAYMENTS_ENABLED && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '1rem',
                        flexWrap: 'wrap',
                    }}
                >
                    <div style={{ flex: 1, minWidth: 260 }}>
                        {stripeLoading && (
                            <div style={{ color: 'var(--gs-text-soft)' }}>
                                {t('settings.payment.stripeCheckingStatus')}
                            </div>
                        )}
                        {!stripeLoading && stripeReady && (
                            <div>
                                <span className="gs-stripe-status gs-stripe-status--ready">
                                    {t('settings.payment.stripeConnected')}
                                </span>
                                <div
                                    style={{
                                        marginTop: '0.4rem',
                                        fontSize: '0.85rem',
                                        color: 'var(--gs-text-muted)',
                                    }}
                                >
                                    {t('settings.payment.stripeChargesPayouts')}
                                    {stripeStatus?.country && ` · ${stripeStatus.country}`}
                                </div>
                                <div
                                    style={{
                                        marginTop: '0.25rem',
                                        fontSize: '0.78rem',
                                        color: 'var(--gs-text-soft)',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    {stripeStatus?.stripeAccountId}
                                </div>
                            </div>
                        )}
                        {!stripeLoading && stripePartial && (
                            <div>
                                <span className="gs-stripe-status gs-stripe-status--partial">
                                    {t('settings.payment.stripePartial')}
                                </span>
                                <div
                                    style={{
                                        marginTop: '0.4rem',
                                        fontSize: '0.85rem',
                                        color: 'var(--gs-text-muted)',
                                    }}
                                >
                                    {t('settings.payment.stripePartialHint')}{' '}
                                    <button
                                        type="button"
                                        data-cy="biz-payment-stripe-refresh-btn"
                                        onClick={refreshFromStripe}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: 'var(--gs-primary-dark)',
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '0.85rem',
                                            fontWeight: 600,
                                            textDecoration: 'underline',
                                        }}
                                    >
                                        {t('settings.payment.stripeRefreshNow')}
                                    </button>
                                </div>
                            </div>
                        )}
                        {!stripeLoading && !stripeStatus?.connected && (
                            <div>
                                <span className="gs-stripe-status gs-stripe-status--off">
                                    {t('settings.payment.stripeNotConnected')}
                                </span>
                                <div
                                    style={{
                                        marginTop: '0.4rem',
                                        fontSize: '0.85rem',
                                        color: 'var(--gs-text-muted)',
                                    }}
                                >
                                    {t('settings.payment.stripeNotConnectedHint')}
                                </div>
                            </div>
                        )}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            gap: '0.5rem',
                            flexShrink: 0,
                            flexWrap: 'wrap',
                        }}
                    >
                        {stripeStatus?.detailsSubmitted && (
                            <button
                                type="button"
                                data-cy="biz-payment-stripe-dashboard-btn"
                                onClick={openStripeDashboard}
                                disabled={!businessId}
                                className="cancel-button"
                                title={t('settings.payment.stripeDashboard')}
                            >
                                {t('settings.payment.stripeDashboard')}
                            </button>
                        )}
                        <button
                            type="button"
                            data-cy="biz-payment-stripe-connect-btn"
                            onClick={startStripeOnboarding}
                            disabled={stripeConnecting || !businessId}
                            className="save-button"
                        >
                            {stripeConnecting
                                ? t('settings.payment.stripeRedirecting')
                                : stripeReady
                                    ? t('settings.payment.stripeUpdateBtn')
                                    : stripeStatus?.connected
                                        ? t('settings.payment.stripeContinueBtn')
                                        : t('settings.payment.stripeConnectBtn')}
                        </button>
                    </div>
                </div>
                )}
            </div>
        </div>
    );
};

export default BusinessPaymentComponent;
